import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import '../styles/MarkdownPage.css';
import '../styles/global.css';

function MarkdownPage({ fileName, title, date, tags = [] }) {
  const [markdownContent, setMarkdownContent] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/articles/${fileName}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch markdown file: ${response.statusText}`);
        }
        return response.text();
      })
      .then((text) => {
        setMarkdownContent(text);
        setError(null);
      })
      .catch((err) => setError(err.message));
  }, [fileName]);

  if (error) {
    return <div>Error loading article: {error}</div>;
  }

  return (
    <div className="page-wrapper">
      <div className="page">
        <article className="article-content mx-auto max-w-5xl overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] shadow-sm">
          <header className="border-b border-[var(--border-color)]">
            <div className="mx-auto max-w-4xl px-6 py-8 md:px-10 md:py-10">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent-gold)] transition hover:text-[var(--text-primary)]">
                <span aria-hidden="true">←</span>
                <span>Back to writings</span>
              </Link>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                {date && (
                  <span className="rounded-full border border-[var(--border-color)] px-3 py-1 text-xs font-semibold text-[var(--text-secondary)]">
                    {date}
                  </span>
                )}
                {tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-[var(--accent-green)] px-3 py-1 text-xs font-semibold text-white">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="article-title mt-5 max-w-4xl text-3xl font-bold leading-tight text-[var(--text-primary)] md:text-5xl">
                {title}
              </h1>
            </div>
          </header>
          <div className="article-body mx-auto max-w-4xl px-6 py-8 md:px-10 md:py-12">
          <Markdown
            children={markdownContent}
            rehypePlugins={[rehypeRaw]}
            components={{
              code(props) {
                const { children, className, ...rest } = props;
                const match = /language-(\w+)/.exec(className || '');
                return match ? (
                  <SyntaxHighlighter
                    {...rest}
                    PreTag="div"
                    children={String(children).replace(/\n$/, '')}
                    language={match[1]}
                    style={vscDarkPlus}
                  />
                ) : (
                  <code {...rest} className={className}>
                    {children}
                  </code>
                );
              },
            }}
          />
          </div>
        </article>
      </div>
    </div>
  );
}

export default MarkdownPage;
